/*
 * Copyright 2026 Sony Corporation
 */

import { z } from 'zod';

/**
 * The scene document and its operations (devplan §3).
 *
 * Shared, and that is the point: the browser's reducer, the agent's tool
 * signatures and the service's document route all validate against *these*
 * objects, so a model's tool call and an application's contract cannot drift
 * apart (§3.2). Every schema is closed, so a field nobody declared cannot
 * arrive from a saved draft or from a language model and be silently ignored by
 * the renderer.
 */

const finite = z.number().refine(Number.isFinite, 'must be a finite number');

/** `[x, y, z]`, in scene units. Arrays rather than objects: a transform is read far more often than it is written. */
export const Vec3Schema = z.tuple([finite, finite, finite]);
export type Vec3 = z.infer<typeof Vec3Schema>;

export const TransformSchema = z.object({ position: Vec3Schema, rotation: Vec3Schema, scale: Vec3Schema }).strict();
export type Transform = z.infer<typeof TransformSchema>;

export const PRIMITIVE_TYPES = ['box', 'sphere', 'cylinder', 'plane', 'ground'] as const;
export type PrimitiveType = (typeof PRIMITIVE_TYPES)[number];

export const LIGHT_TYPES = ['directional', 'point', 'spot'] as const;
export type LightType = (typeof LIGHT_TYPES)[number];

export const RESOLUTIONS = ['1k', '2k', '4k'] as const;
export type Resolution = (typeof RESOLUTIONS)[number];

/**
 * Poly Haven's own id shape, which a Sketchfab uid also satisfies. Checked
 * before it is ever interpolated into a URL, wherever that happens — the
 * browser's importer or the agent's tool.
 *
 * Case matters: HDRIs and textures are lower case, but models are not
 * (`GreenChair_01`), so a lower-case-only rule rejects half the catalogue.
 */
export const SlugSchema = z.string().regex(/^[A-Za-z0-9_-]+$/, 'must be an asset slug or uid');

/** Where an asset came from, which decides how a reference is turned into bytes (devplan §5.5, §5.8). */
export const ASSET_SOURCES = ['polyhaven', 'sketchfab'] as const;
export const AssetSourceSchema = z.enum(ASSET_SOURCES);
export type AssetSource = z.infer<typeof AssetSourceSchema>;

/** Opaque and stable: renaming a node never changes it, so a name collision is harmless. */
export const NodeIdSchema = z.string().regex(/^nd_[A-Za-z0-9]+$/, 'must be a node id such as nd_7f3a91');

const NodeBase = { id: NodeIdSchema, name: z.string().min(1).max(64), transform: TransformSchema };

/**
 * A reference, never a URL (devplan §3.1).
 *
 * One function turns this triple into somewhere to fetch from, which is what
 * keeps a slug safe to let a language model produce — and what makes swapping
 * direct loading for a mirror a one-file change.
 */
export const AssetRefSchema = z
  .object({
    source: AssetSourceSchema,
    slug: SlugSchema,
    resolution: z.enum(RESOLUTIONS),
    /** Poly Haven's `files_hash` when it was added: provenance until the mirror can enforce it. */
    pinned_hash: z.string().optional(),
  })
  .strict();
export type AssetRef = z.infer<typeof AssetRefSchema>;

export const PrimitiveNodeSchema = z
  .object({
    ...NodeBase,
    kind: z.literal('primitive'),
    // One number, not width/height/depth: these are blocking-out shapes, and
    // anything else is expressed by the transform's scale.
    primitive: z.object({ type: z.enum(PRIMITIVE_TYPES), size: finite.positive() }).strict(),
    /** A Poly Haven texture set. Only primitives take one: an imported model brings its own. */
    material: AssetRefSchema.optional(),
  })
  .strict();
export type PrimitiveNode = z.infer<typeof PrimitiveNodeSchema>;

export const LightNodeSchema = z
  .object({
    ...NodeBase,
    kind: z.literal('light'),
    light: z.object({ type: z.enum(LIGHT_TYPES), intensity: finite.nonnegative() }).strict(),
  })
  .strict();
export type LightNode = z.infer<typeof LightNodeSchema>;

export const AssetNodeSchema = z.object({ ...NodeBase, kind: z.literal('asset'), asset: AssetRefSchema }).strict();
export type AssetNode = z.infer<typeof AssetNodeSchema>;

export const SceneNodeSchema = z.discriminatedUnion('kind', [PrimitiveNodeSchema, LightNodeSchema, AssetNodeSchema]);
export type SceneNode = z.infer<typeof SceneNodeSchema>;

/** The scene's image-based lighting and sky, which for most scenes is all the lighting there is. */
export const EnvironmentSchema = z
  .object({ hdri: AssetRefSchema, intensity: finite.nonnegative(), rotation_y: finite })
  .strict();
export type Environment = z.infer<typeof EnvironmentSchema>;

export const SceneDocSchema = z
  .object({
    doc_version: z.literal(1),
    units: z.literal('m'),
    nodes: z.array(SceneNodeSchema),
    environment: EnvironmentSchema.optional(),
  })
  .strict();
export type SceneDoc = z.infer<typeof SceneDocSchema>;

/**
 * The operations, and the only way a document changes.
 *
 * The gizmo, the panels and the agent all emit these, into the same reducer
 * with the same validation and the same undo stack (devplan §3.2).
 */

export const AddPrimitiveOpSchema = z
  .object({
    op: z.literal('add_primitive'),
    type: z.enum(PRIMITIVE_TYPES),
    size: finite.positive().optional(),
    position: Vec3Schema.optional(),
  })
  .strict();

export const AddLightOpSchema = z
  .object({
    op: z.literal('add_light'),
    type: z.enum(LIGHT_TYPES),
    intensity: finite.nonnegative().optional(),
    position: Vec3Schema.optional(),
  })
  .strict();

export const AddAssetOpSchema = z
  .object({
    op: z.literal('add_asset'),
    source: AssetSourceSchema,
    slug: SlugSchema,
    resolution: z.enum(RESOLUTIONS).optional(),
    pinned_hash: z.string().optional(),
    name: z.string().min(1).max(64).optional(),
    position: Vec3Schema.optional(),
  })
  .strict();

/** `hdri: null` clears it, which is the only way back to no environment at all. */
export const SetEnvironmentOpSchema = z
  .object({
    op: z.literal('set_environment'),
    slug: SlugSchema.nullable(),
    resolution: z.enum(RESOLUTIONS).optional(),
    pinned_hash: z.string().optional(),
    intensity: finite.nonnegative().optional(),
    rotation_y: finite.optional(),
  })
  .strict();

/** `slug: null` strips it back to the editor's own grey. */
export const SetMaterialOpSchema = z
  .object({
    op: z.literal('set_material'),
    node_ids: z.array(NodeIdSchema).min(1),
    slug: SlugSchema.nullable(),
    resolution: z.enum(RESOLUTIONS).optional(),
    pinned_hash: z.string().optional(),
  })
  .strict();

export const SetTransformOpSchema = z
  .object({
    op: z.literal('set_transform'),
    node_id: NodeIdSchema,
    position: Vec3Schema.optional(),
    rotation: Vec3Schema.optional(),
    scale: Vec3Schema.optional(),
  })
  .strict();

export const DuplicateOpSchema = z
  .object({ op: z.literal('duplicate'), node_ids: z.array(NodeIdSchema).min(1) })
  .strict();

export const DeleteOpSchema = z.object({ op: z.literal('delete'), node_ids: z.array(NodeIdSchema).min(1) }).strict();

export const SceneOpSchema = z.discriminatedUnion('op', [
  AddPrimitiveOpSchema,
  AddLightOpSchema,
  AddAssetOpSchema,
  SetEnvironmentOpSchema,
  SetMaterialOpSchema,
  SetTransformOpSchema,
  DuplicateOpSchema,
  DeleteOpSchema,
]);
export type SceneOp = z.infer<typeof SceneOpSchema>;

/**
 * What the agent is told about the scene (devplan §7.3).
 *
 * Ids, names, kinds, rounded positions and sizes — and nothing else. Not the
 * document, because a document carries material references and pinned hashes
 * that cost tokens and decide nothing; never a URL, a token or raw `.babylon`
 * bytes.
 *
 * A schema rather than a type because it crosses the socket: the browser
 * computes it and the service has to parse it like any other request body.
 */

/** Beyond this the digest summarises, and the model is told how many it is not seeing. */
export const MAX_DIGEST_NODES = 200;

export const DigestNodeSchema = z
  .object({
    id: NodeIdSchema,
    name: z.string().max(64),
    kind: z.enum(['primitive', 'light', 'asset']),
    /** The primitive shape, the light type, or the asset's slug. */
    type: z.string().max(64),
    position: Vec3Schema,
    /** Absent for an asset, whose extent is unknown until it has loaded. */
    size: finite.optional(),
  })
  .strict();
export type DigestNode = z.infer<typeof DigestNodeSchema>;

export const SceneDigestSchema = z
  .object({
    units: z.literal('m'),
    /** Every distance an agent proposes is relative to this, never to a constant. */
    bounds: z.object({ center: Vec3Schema, radius: finite.nonnegative() }).strict(),
    /** The HDRI slug, when the scene has one. */
    environment: z.string().optional(),
    selection: z.array(NodeIdSchema),
    nodes: z.array(DigestNodeSchema).max(MAX_DIGEST_NODES),
    /** How many nodes were left out, when there are more than the cap. */
    omitted: z.number().int().optional(),
  })
  .strict();
export type SceneDigest = z.infer<typeof SceneDigestSchema>;
